import Logger from '../modules/logger.js'
import { Reference } from './referencePool.js'
import MsecDuratives from './MsecDuratives.js';
import TimeValue from './TimeValue.js';

const referenceKeyDelimiter = '::';
const supportedDataVersions = [ '0.5' ];
const generatingDataVersion = '0.5';

function generateUniqueId(existingIds, { count = 0, prefix = undefined } = {}) {
  count = Math.floor(count);
  if (prefix !== undefined) {
    let uniqueId = prefix;
    if (count > 0) uniqueId += String(count);
    if (!existingIds.includes(uniqueId)) {
      return uniqueId;
    }
  } else {
    let uniqueId = String(count);
    if (!existingIds.includes(uniqueId)) {
      return uniqueId
    }
  }
  return generateUniqueId(existingIds, { count: count + 1, prefix });
}

class ScattData {
  static name = 'ScattData';

  constructor(scattDataRawObj, segmentDataTypes) {
    if (!supportedDataVersions.includes(scattDataRawObj.data_version)) {
      Logger.error(`Data version ${scattDataRawObj.data_version} is not supported`);
      return;
    }
    for (let timelineDataId in scattDataRawObj.timeline_data_set) {
      if (timelineDataId.includes(referenceKeyDelimiter)) {
        Logger.error(`Can not contain ${referenceKeyDelimiter} in timeline data ID(${timelineDataId})`);
        return;
      }
      let timelineData = scattDataRawObj.timeline_data_set[timelineDataId];
      for (let segmentId in timelineData) {
        if (segmentId.includes(referenceKeyDelimiter)) {
          Logger.error(`Can not contain ${referenceKeyDelimiter} in segment ID(${segmentId})`);
          return;
        }
      }
    }

    this.dataVersion = scattDataRawObj.data_version;
    this.timelineDataSet = new TimelineDataSet();
    if (Object.keys(scattDataRawObj).includes('timeline_data_set')) {
      for (let timelineDataRawObj of Object.values(scattDataRawObj.timeline_data_set)) {
        let timelineDataId = this.timelineDataSet.generateNewId();
        let timelineData = TimelineData.generateFromRawObj(timelineDataRawObj, segmentDataTypes);
        if (timelineData === undefined) continue;
        this.timelineDataSet[timelineDataId] = timelineData;
      }
    }
    this.timelineDataSet.normalizeTimelineDataIdcs();
  }

  static loadReferences(scattDataRawObj) {
    return Object.values(scattDataRawObj.references).map(timelineSegmentIdRawObj => {
      let timelineSegmentIdReferFrom = TimelineSegmentId.generateFromRawObj(timelineSegmentIdRawObj.refer_from);
      let timelineSegmentIdReferTo = TimelineSegmentId.generateFromRawObj(timelineSegmentIdRawObj.refer_to);
      return new Reference(timelineSegmentIdRawObj.ref_class, timelineSegmentIdReferFrom, timelineSegmentIdReferTo);
    });
  }

  generateRawObj(allReferences) {
    let scattDataRawObj = {};
    scattDataRawObj.data_version = generatingDataVersion;
    scattDataRawObj.timeline_data_set = {};
    for (let [ timelineDataId, timelineData ] of Object.entries(this.timelineDataSet)) {
      scattDataRawObj.timeline_data_set[timelineDataId] = timelineData.generateRawObj();
    }
    scattDataRawObj.references = [];
    if (allReferences) {
      for (let reference of Object.values(allReferences)) {
        let referenceRawObj = {};
        referenceRawObj.ref_class = reference.refClass;
        referenceRawObj.refer_from = reference.referFrom.mergedId;
        referenceRawObj.refer_to = reference.referTo.mergedId;
        scattDataRawObj.references.push(referenceRawObj);
      }
    }
    return scattDataRawObj;
  }

  static generateEmpty() {
    return new ScattData(ScattData.generateEmptyRawObj(), []);
  }

  static generateEmptyRawObj() {
    return { data_version: generatingDataVersion, timeline_data_set: {}, references: [] };
  }

  clone(isRetainingLocked) {
    let newScattData = ScattData.generateEmpty();
    for (let [ timelineDataId, timelineData ] of Object.entries(this.timelineDataSet)) {
      newScattData.timelineDataSet[timelineDataId] = timelineData.clone(isRetainingLocked);
    }
    return newScattData;
  }
}

class TimelineDataDifferencePair {
  static name = 'TimelineDataDifferencePair';

  constructor(before = {}, after = {}) {
    this.before = before;
    this.after = after;
  }
}

class TimelineDataSet extends Object {
  static name = 'TimelineDataSet';

  get ids() {
    return Object.keys(this);
  }

  get nextIdx() {
    return this.ids.length;
  }

  generateNewId(timelineDataId = undefined) {
    return generateUniqueId(this.ids, { prefix: timelineDataId });
  }

  generateEmptyTimelineData(name, segmentDataType, authorId, authorName, { hidden = undefined, readonly = undefined, locked = undefined } = {}) {
    return TimelineData.generateEmpty(
      name,
      segmentDataType,
      authorId,
      authorName,
      this.nextIdx,
      { hidden, readonly, locked },
    );
  }

  getSegment(timelineSegmentId) {
    let timelineData = this[timelineSegmentId.timelineDataId];
    if (!timelineData) return undefined;
    if (!Object.keys(timelineData.segments).includes(timelineSegmentId.segmentId)) return undefined;
    return timelineData.segments[timelineSegmentId.segmentId];
  }

  isTimelineSegmentImmutable(timelineSegmentId) {
    let timelineData = this[timelineSegmentId.timelineDataId];
    return timelineData.isSegmentImmutable(timelineSegmentId.segmentId);
  }

  moveTimelineDataIdxTo(timelineDataIdxFrom, timelineDataIdxTo) {
    if (timelineDataIdxFrom === timelineDataIdxTo) return;
    let timelineDataIdFrom = this.ids.find(timelineDataId => (this[timelineDataId].timelineDataIdx === timelineDataIdxFrom));
    if (timelineDataIdxFrom < timelineDataIdxTo) {
      let timelineDataIdxDecrementGroupBegin = timelineDataIdxFrom + 1;
      let timelineDataIdxDecrementGroupEnd = timelineDataIdxTo + 1;
      for (let timelineDataIdx = timelineDataIdxDecrementGroupBegin; timelineDataIdx < timelineDataIdxDecrementGroupEnd; ++timelineDataIdx) {
        let timelineDataId = this.ids.find(timelineDataId => (this[timelineDataId].timelineDataIdx === timelineDataIdx));
        this[timelineDataId].timelineDataIdx = timelineDataIdx - 1;
      }
    } else {
      let timelineDataIdxIncrementGroupReversedBegin = timelineDataIdxFrom - 1;
      let timelineDataIdxIncrementGroupReversedEnd = timelineDataIdxTo - 1;
      for (let timelineDataIdx = timelineDataIdxIncrementGroupReversedBegin; timelineDataIdx > timelineDataIdxIncrementGroupReversedEnd; --timelineDataIdx) {
        let timelineDataId = this.ids.find(timelineDataId => (this[timelineDataId].timelineDataIdx === timelineDataIdx));
        this[timelineDataId].timelineDataIdx = timelineDataIdx + 1;
      }
    }
    this[timelineDataIdFrom].timelineDataIdx = timelineDataIdxTo;
  }

  normalizeTimelineDataIdcs() {
    let timelineDataByIdx = {};
    for (let timelineData of Object.values(this)) {
      timelineDataByIdx[String(timelineData.timelineDataIdx)] = timelineData;
    }
    let sortedExistingTimelineDataIdcs = Object.keys(timelineDataByIdx).map(Number).sort();
    let numTimelineData = sortedExistingTimelineDataIdcs.length;
    for (let newTimelineDataIdx = 0; newTimelineDataIdx < numTimelineData; ++newTimelineDataIdx) {
      let oldTimelineDataIdx = sortedExistingTimelineDataIdcs[newTimelineDataIdx];
      timelineDataByIdx[oldTimelineDataIdx].timelineDataIdx = newTimelineDataIdx;
    }
  }
}

class TimelineData {
  static name = 'TimelineData';

  constructor(
    name,
    segmentDataType,
    segments,
    createdAt,
    modifiedAt,
    authorId,
    authorName,
    timelineDataIdx,
    hidden,
    readonly,
    locked,
  )
  {
    this.name = name;
    this.segmentDataType = segmentDataType;
    this.segments = segments;
    this.createdAt = new Date(createdAt);
    this.modifiedAt = new Date(modifiedAt);
    this.authorId = authorId;
    this.authorName = authorName;
    this.hidden = hidden;
    this.locked = locked;
    this.readonly = readonly;
    this.timelineDataIdx = timelineDataIdx;
  }

  generateRawObj() {
    let timelineDataRawObj = {};
    timelineDataRawObj.name = this.name;
    timelineDataRawObj.segment_data_type = String(this.segmentDataType);
    timelineDataRawObj.segments = {};
    for (let [ segmentId, segment ] of Object.entries(this.segments)) {
      timelineDataRawObj.segments[segmentId] = segment.generateRawObj();
    }
    timelineDataRawObj.created_at = this.createdAt.toISOString();
    timelineDataRawObj.modified_at = this.modifiedAt.toISOString();
    timelineDataRawObj.author_id = this.authorId;
    timelineDataRawObj.author_name = this.authorName;
    timelineDataRawObj.timeline_data_idx = this.timelineDataIdx;
    timelineDataRawObj.hidden = this.hidden;
    timelineDataRawObj.readonly = this.readonly;
    timelineDataRawObj.locked = this.locked;
    return timelineDataRawObj;
  }

  static generateEmpty(name, segmentDataType, authorId, authorName, timelineDataIdx, { hidden = false, readonly = false, locked = false } = {}) {
    let createdAt = new Date();
    let modifiedAt = new Date(createdAt);
    return new TimelineData(
      name,
      segmentDataType,
      {},
      createdAt,
      modifiedAt,
      authorId,
      authorName,
      timelineDataIdx,
      hidden,
      readonly,
      locked,
    );
  }

  static constructSegmentData(segmentDataType, segmentsRawObj) {
    let segments = {};
    for (let [ segmentId, segmentRawObj ] of Object.entries(segmentsRawObj)) {
      let segment = segmentDataType.generateFromRawObj(segmentRawObj.begin, segmentRawObj.end, segmentRawObj.data);
      segments[segmentId] = segment;
    }
    return segments;
  }

  static getSegmentDataType(segmentDataTypeString, segmentDataTypes) {
    /***************************************************************
     * NOTE: Don't use .name property! (Webpack minifies class name)
     ***************************************************************/
    let foundSegmentDataType = segmentDataTypes.find(segmentDataType => (String(segmentDataType) === segmentDataTypeString));
    if (foundSegmentDataType === undefined) return undefined;
    return foundSegmentDataType;
  }

  static generateFromRawObj(timelineDataRawObj, segmentDataTypes) {
    let segmentDataType = TimelineData.getSegmentDataType(timelineDataRawObj.segment_data_type, segmentDataTypes);
    if (segmentDataType === undefined) return undefined;
    let segments = TimelineData.constructSegmentData(segmentDataType, timelineDataRawObj.segments);
    return new TimelineData(
      timelineDataRawObj.name,
      segmentDataType,
      segments,
      timelineDataRawObj.created_at,
      timelineDataRawObj.modified_at,
      timelineDataRawObj.author_id,
      timelineDataRawObj.author_name,
      timelineDataRawObj.timeline_data_idx,
      timelineDataRawObj.hidden,
      timelineDataRawObj.readonly,
      timelineDataRawObj.locked,
    );
  }

  get segmentIds() {
    return Object.keys(this.segments);
  }

  get numSegments() {
    return this.segmentIds.length;
  }

  generateNewId() {
    return generateUniqueId(this.segmentIds);
  }

  clone(isRetainingLocked) {
    let newSegments = {};
    for (let [ segmentId, segment ] of Object.entries(this.segments)) {
      newSegments[segmentId] = segment.clone(isRetainingLocked);
    }
    let newTimelineData = new TimelineData(
      this.name,
      this.segmentDataType,
      newSegments,
      this.createdAt,
      this.modifiedAt,
      this.authorId,
      this.authorName,
      this.timelineDataIdx,
      this.hidden,
      (isRetainingLocked)? this.readonly : false,
      (isRetainingLocked)? this.locked : false,
    );
    return newTimelineData;
  }

  isSegmentImmutable(segmentId) {
    if (this.readonly) return true;
    if (this.locked) return true;
    if (this.segments[segmentId].locked) return true;
    return false;
  }

  getDifferencePair(that) {
    let before = {};
    let after = {};
    if (this.segmentDataType !== that.segmentDataType) {
      before.segmentDataType = this.segmentDataType;
      after.segmentDataType = that.segmentDataType;
    }
    if (this.timelineDataIdx !== that.timelineDataIdx) {
      before.timelineDataIdx = this.timelineDataIdx;
      after.timelineDataIdx = that.timelineDataIdx;
    }
    if (this.name !== that.name) {
      before.name = this.name;
      after.name = that.name;
    }
    if (this.hidden !== that.hidden) {
      before.hidden = this.hidden;
      after.hidden = that.hidden;
    }
    if (this.locked !== that.locked) {
      before.locked = this.locked;
      after.locked = that.locked;
    }
    let wholeSegmentIdSet = new Set(this.segmentIds.concat(that.segmentIds));
    before.segments = {};
    after.segments = {};
    for (let segmentId of wholeSegmentIdSet) {
      let thisSegment = this.segments[segmentId];
      let thatSegment = that.segments[segmentId];
      if ((thisSegment !== undefined) && (thatSegment !== undefined)) {
        if (!thisSegment.isSameAs(thatSegment)) {
          before.segments[segmentId] = thisSegment.clone();
          after.segments[segmentId] = thatSegment.clone();
        }
      } else if (thisSegment !== undefined) {
        before.segments[segmentId] = thisSegment.clone();
        after.segments[segmentId] = undefined;
      } else if (thatSegment !== undefined) {
        before.segments[segmentId] = undefined;
        after.segments[segmentId] = thatSegment.clone();
      }
    }
    if (Object.keys(before.segments).length === 0) delete before.segments;
    if (Object.keys(after.segments).length === 0) delete after.segments;
    if ((Object.keys(before).length === 0) && (Object.keys(after).length === 0)) {
      return undefined;
    } else {
      return new TimelineDataDifferencePair(before, after);
    }
  }

  isSameAs(that) {
    if (this.segmentDataType !== that.segmentDataType) return false;
    if (this.timelineDataIdx !== that.timelineDataIdx) return false;
    if (this.name !== that.name) return false;
    if (this.hidden !== that.hidden) return false;
    if (this.locked !== that.locked) return false;
    if (this.numSegments !== that.numSegments) return false;
    for (let thisSegmentid of this.segmentIds) {
      if (!that.segmentIds.includes(thisSegmentid)) return false;
    }
    for (let thatSegmentid of that.segmentIds) {
      if (!this.segmentIds.includes(thatSegmentid)) return false;
    }
    for (let segmentId of this.segmentIds) {
      if (!this.segments[segmentId].isSameAs(that.segments[segmentId])) return false;
    }
    return true;
  }

  getBeginTimeAscendingOrderIdFunction() {
    return (segmentIdA, segmentIdB) => {
      let segmentA = this.segments[segmentIdA];
      let segmentB = this.segments[segmentIdB];
      return (segmentA.begin === null)? -1 : (segmentA.begin - segmentB.begin)
    };
  }

  getEndTimeAscendingOrderIdFunction() {
    return (segmentIdA, segmentIdB) => {
      let segmentA = this.segments[segmentIdA];
      let segmentB = this.segments[segmentIdB];
      return (segmentA.end === null)? -1 : (segmentA.end - segmentB.end)
    };
  }
}

class TimelineSegmentId {
  static name = 'TimelineSegmentId';

  constructor(timelineDataId, segmentId) {
    this.timelineDataId = timelineDataId;
    this.segmentId = segmentId;
  }

  get mergedId() {
    return this.timelineDataId + referenceKeyDelimiter + this.segmentId;
  }

  clone() {
    return new TimelineSegmentId(this.timelineDataId, this.segmentId);
  }

  isSameAs(otherTimelineSegmentId) {
    if (this.timelineDataId !== otherTimelineSegmentId.timelineDataId) return false;
    if (this.segmentId !== otherTimelineSegmentId.segmentId) return false;
    return true;
  }

  static generateFromRawObj(timelineSegmentIdRawObj) {
    return new TimelineSegmentId(...timelineSegmentIdRawObj.split(referenceKeyDelimiter, 2));
  }
}

class TimelineSegmentContentDataBase {
  static name = 'TimelineSegmentContentDataBase';

  drawOnTimelineSegment({ canvas2d, rect, paddingPx }) {
    const segmentFontSizePxMax = 12;
    const segmentTextOffsetPx = 5;
    const segmentTextOmissionIndicator = '...';

    let text = this.toString();
    let textFontSizePx = rect.height - 4;
    let textLeftX = rect.x + segmentTextOffsetPx;
    let textMiddleY = rect.y + rect.height / 2;
    let textWidthPxMax = rect.x + rect.width - (textLeftX + paddingPx * 2);
    canvas2d.textBaseline = 'middle';
    canvas2d.font = `normal ${(textFontSizePx > segmentFontSizePxMax) ? segmentFontSizePxMax : textFontSizePx}px sans-serif`;
    let textWidthPx = canvas2d.measureText(text).width;
    if (textWidthPx < textWidthPxMax) {
      canvas2d.fillText(text, textLeftX, textMiddleY);
    } else {
      let segmentTextOmissionIndicatorWidthPx = canvas2d.measureText(segmentTextOmissionIndicator).width;
      if (segmentTextOmissionIndicatorWidthPx > textWidthPxMax) {
        canvas2d.fillText(segmentTextOmissionIndicator, textLeftX, textMiddleY, textWidthPxMax);
      } else {
        let subTextLastIdxOfFirstOverflow = searchSubTextLastIdxOfFirstOverflow(canvas2d, text, textWidthPxMax);
        let subTextOfFirstOverflow = text.substring(0, subTextLastIdxOfFirstOverflow);
        let subTextOfFirstOverflowWithOmissionIndicator = subTextOfFirstOverflow + segmentTextOmissionIndicator;
        canvas2d.fillText(subTextOfFirstOverflowWithOmissionIndicator, textLeftX, textMiddleY);
      }
    }

    function searchSubTextLastIdxOfFirstOverflow(canvas2d, text, textWidthPxMax) {
      let isOverflowedAt = (idx) => {
        let subTextWithOmissionIndicator = text.substring(0, idx) + segmentTextOmissionIndicator;
        let subTextWithOmissionIndicatorWidthPx = canvas2d.measureText(subTextWithOmissionIndicator).width;
        return (subTextWithOmissionIndicatorWidthPx > textWidthPxMax);
      };
      let leftIdx = 0;
      let rightIdx = text.length - 2;
      while (leftIdx <= rightIdx) {
        let middleIdx = Math.floor((leftIdx + rightIdx) / 2);
        if (!isOverflowedAt(middleIdx) && isOverflowedAt(middleIdx + 1)) {
          return middleIdx;
        } else {
          if (isOverflowedAt(middleIdx) && isOverflowedAt(middleIdx + 1)) {
            rightIdx = middleIdx - 1;
          } else if (!isOverflowedAt(middleIdx) && !isOverflowedAt(middleIdx + 1)) {
            leftIdx = middleIdx + 1;
          }
        }
      }
      return (text.length - 2);
    }
  }

  toString() {
    return '';
  }

  /* eslint-disable no-unused-vars */
  combine(that) {
    return;
  }
  /* eslint-enable no-unused-vars */
}

class TimelineSegmentDataBase extends MsecDuratives {
  static contentData = TimelineSegmentContentDataBase;

  constructor(begin, end, data, isLocked) {
    super(begin, end);
    this.data = data;
    this.locked = isLocked;
  }

  static generateFromRawObj(beginMsec, endMsec, dataRawObj) {
    return new this(
      (beginMsec === null)? null : TimeValue.msec(beginMsec),
      (endMsec === null)? null : TimeValue.msec(endMsec),
      this.contentData.generateFromRawObj(dataRawObj),
      false,
    );
  }

  static generateEmpty(begin, end) {
    return new this(
      (begin === null)? null : begin.clone(),
      (end === null)? null : end.clone(),
      new this.contentData(),
      false,
    );
  }

  clone(isRetainingLocked) {
    let newSegmentData = super.clone();
    newSegmentData.data = this.data.clone();
    newSegmentData.locked = (isRetainingLocked)? false : this.locked;
    return newSegmentData;
  }

  generateRawObj() {
    let timelineSegmentDataBase = super.generateRawObj();
    timelineSegmentDataBase.data = this.data.generateRawObj();
    timelineSegmentDataBase.locked = this.locked;
    return timelineSegmentDataBase;
  }

  isSameAs(that) {
    return super.isSameAs(that) && this.data.isSameAs(that.data) && (this.locked === that.locked);
  }

  combine(that) {
    {
      if (isThisBeginEarlierThanThatBegin.call(this, that)) {
        this.data.combine(that.data);
      } else {
        let thatData = that.data.clone();
        thatData.combine(this.data);
        this.data = thatData;
      }
      if (isThisEndEarlierThanThatEnd.call(this, that)) {
        this.end = that.end;
      }
    }

    function isThisBeginEarlierThanThatBegin(that) {
      if (this.begin === null) return true;
      if (that.begin === null) return false;
      return this.begin.isLessThanOrEqualTo(that.begin);
    }

    function isThisEndEarlierThanThatEnd(that) {
      if (this.end === null) return true;
      if (that.end === null) return false;
      return this.end.isLessThanOrEqualTo(that.end);
    }
  }
}

export {
  ScattData,
  TimelineDataSet,
  TimelineSegmentId,
  TimelineData,
  TimelineSegmentDataBase,
  TimelineSegmentContentDataBase,
};
